<script setup>
import axios from 'axios';
import { ref } from 'vue';
import { useRoute } from 'vue-router';

import SearchSvg from '@images/empty-search-md.svg';
import ErrorSvg from '@images/status-fail-md.svg';

import ContributorsTable from '~/common/components/ContributorsTable.vue';
import FilterCard from '~/common/components/FilterCard.vue';
import { DEFAULT_TITLE } from '~/common/Constants';

const route = useRoute();
const { organizationName } = route.params;
const organization = ref({ name: organizationName, contributors: [] });
const totals = ref(null);
const busy = ref(true);
const error = ref(null);

const filterData = ref({
  fromDate: '',
  toDate: '',
});

const fetchData = async () => {
  const name = organization.value.name;
  const params = {
    from_date: filterData.value.fromDate,
    to_date: filterData.value.toDate,
    id: name,
  };
  error.value = null;

  let response;
  try {
    response = await axios.get(
      '/api/v1/organizations',
      { params },
    );
  } catch (e) {
    error.value = e;
  }

  if (response?.status === 200) {
    document.title = `${organizationName} - ${DEFAULT_TITLE}`;
    organization.value = response.data;
    if (organization.value.contributors.length > 1) {
      totals.value = calculateTotal();
    }

    busy.value = false;
  }
};

const calculateTotal = () => {
  const initSum = {
    merged_merge_requests: 0,
    merged_with_issues: 0,
    opened_merge_requests: 0,
    opened_issues: 0,
    merged_commits: 0,
    added_notes: 0,
    bonus_points: 0,
    score: 0,
  };
  const contributors = organization.value.contributors;

  return contributors.reduce((sum, contributor) => {
    Object.keys(sum).forEach((contributionType) => {
      sum[contributionType] += contributor[contributionType];
    });

    return sum;
  }, initSum);
};
</script>

<template>
  <div
    v-if="error?.status === 404"
    class="text-center"
  >
    <img
      :src="SearchSvg"
      alt="Magnifying glass"
    >
    <h2 class="fw-bold">Organization not found.</h2>
  </div>

  <div
    v-else-if="error"
    class="text-center"
  >
    <img
      :src="ErrorSvg"
      alt="Red x"
    >
    <h2 class="fw-bold">Something went wrong.</h2>
  </div>

  <template v-else>
    <div>
      <h1 class="my-4">
        {{ organization.name }}
      </h1>

      <FilterCard
        v-model="filterData"
        class="my-3"
        @filter="fetchData"
      />

      <h2>Contributors</h2>
      <ContributorsTable
        :contributors="organization.contributors"
        :totals="totals"
        :busy="busy"
        :filter-data="filterData"
      />
    </div>
  </template>
</template>
